'use client';

import { useState } from 'react';
import { Camera, X, Image as ImageIcon } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import BetterUploader from '@/components/BetterUploader';
import { FilePickerDialog } from '@/components/files';
import { cn } from '@/lib/utils';

interface MediaSelectorProps {
  value: string | string[] | null;
  onChange: (url: string | string[] | null) => void;
  label?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  shape?: 'circle' | 'square';
  showBrowseLibrary?: boolean;
  mode?: 'selector-only' | 'single-image' | 'gallery';
  showImages?: boolean;
  allowMultiple?: boolean;
}

const sizeClasses = {
  sm: 'w-16 h-16',
  md: 'w-20 h-20',
  lg: 'w-24 h-24',
};

const iconSizeClasses = {
  sm: 'w-6 h-6',
  md: 'w-8 h-8',
  lg: 'w-10 h-10',
};

export function MediaSelector({
  value,
  onChange,
  label = 'Image',
  helperText = 'JPG, PNG or GIF. Max 5MB.',
  size = 'md',
  shape = 'circle',
  showBrowseLibrary = true,
  mode = 'single-image',
  showImages = true,
  allowMultiple = false,
}: MediaSelectorProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [showFilePicker, setShowFilePicker] = useState(false);

  const isGalleryMode = mode === 'gallery';
  const isSelectorOnly = mode === 'selector-only';
  const isMultipleMode = isGalleryMode || (isSelectorOnly && allowMultiple);
  const valueArray = isMultipleMode ? (Array.isArray(value) ? value : value ? [value] : []) : [];
  const singleValue = !isMultipleMode ? (Array.isArray(value) ? value[0] : value) : null;

  const handleImageUpload = (data: any) => {
    const url = data?.metadata?.files[0]?.url;
    if (url) {
      if (isMultipleMode) {
        onChange([...valueArray, url]);
      } else {
        onChange(url);
      }
      setIsUploading(false);
    }
  };

  const handleRemoveImage = (index?: number) => {
    if (isMultipleMode && typeof index === 'number') {
      const newArray = valueArray.filter((_, i) => i !== index);
      onChange(newArray.length > 0 ? newArray : null);
    } else {
      onChange(null);
    }
  };

  const handleFileSelect = (files: any[]) => {
    if (files && files.length > 0) {
      if (isMultipleMode) {
        const urls = files.map((f) => f.url);
        onChange([...valueArray, ...urls]);
      } else {
        onChange(files[0].url);
      }
    }
    setShowFilePicker(false);
  };

  const shapeClass = shape === 'circle' ? 'rounded-full' : 'rounded-lg';

  return (
    <>
      <div className="grid gap-2">
        <Label>{label}</Label>
        <div className="flex items-start gap-4">
          {/* Image Preview - Only show if not selector-only mode and showImages is true */}
          {!isSelectorOnly && showImages && (
            <>
              {isGalleryMode ? (
                // Gallery Mode - Show multiple images
                <div className="flex flex-wrap gap-2">
                  {valueArray.map((url, index) => (
                    <div key={index} className="relative flex-shrink-0">
                      <div
                        className={cn(
                          sizeClasses[size],
                          shapeClass,
                          'overflow-hidden bg-gray-100 flex items-center justify-center border-2 border-gray-200'
                        )}
                      >
                        <img
                          src={url}
                          alt={`${label} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors cursor-pointer"
                        title="Remove image"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {valueArray.length === 0 && (
                    <div
                      className={cn(
                        sizeClasses[size],
                        shapeClass,
                        'overflow-hidden bg-gray-100 flex items-center justify-center border-2 border-gray-200'
                      )}
                    >
                      <Camera className={cn(iconSizeClasses[size], 'text-gray-400')} />
                    </div>
                  )}
                </div>
              ) : (
                // Single Image Mode
                <div className="relative flex-shrink-0">
                  <div
                    className={cn(
                      sizeClasses[size],
                      shapeClass,
                      'overflow-hidden bg-gray-100 flex items-center justify-center border-2 border-gray-200'
                    )}
                  >
                    {singleValue ? (
                      <img src={singleValue} alt={label} className="w-full h-full object-cover" />
                    ) : (
                      <Camera className={cn(iconSizeClasses[size], 'text-gray-400')} />
                    )}
                  </div>
                  {singleValue && (
                    <button
                      type="button"
                      onClick={() => handleRemoveImage()}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors cursor-pointer"
                      title="Remove image"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Upload Controls */}
          <div className="flex-1 space-y-1.5">
            {/* Buttons Row */}
            <div className="flex gap-2">
              <BetterUploader
                mode="single"
                accept="image/*"
                onUploadComplete={handleImageUpload}
                onUploadProgress={(data) => {
                  if (data?.progress > 0) {
                    setIsUploading(true);
                  }
                }}
              />
              {showBrowseLibrary && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilePicker(true)}
                  className="cursor-pointer h-8 text-xs px-3"
                >
                  <ImageIcon className="w-3.5 h-3.5 mr-1.5" />
                  Browse
                </Button>
              )}
            </div>

            {isUploading && <p className="text-xs text-gray-500">Uploading...</p>}
            <p className="text-xs text-gray-500">{helperText}</p>
          </div>
        </div>
      </div>

      {/* File Picker Dialog */}
      {showBrowseLibrary && (
        <FilePickerDialog
          open={showFilePicker}
          onOpenChange={setShowFilePicker}
          onSelect={handleFileSelect}
          selectionMode={isMultipleMode ? 'multiple' : 'single'}
          title="Select Image from Library"
          initialType={['media']}
          initialStatus="active"
        />
      )}
    </>
  );
}
